import type { Command, JsonValue, RecordDefinition } from "@e308/core";
import { rejectUnlessActiveBorder } from "./border-guards.js";
import { rejectIfEliminated } from "./command-guards.js";
import type { SeatModel } from "./content-types.js";
import { appendPactAction, cloneDiplomacy, SHIPMENT_TRAVEL_MS } from "./diplomacy-state.js";
import type { DiplomacyState, MatchLogPayload } from "./diplomacy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import type { ConflictIntent } from "./types.js";

type DiplomacyIntent = Extract<
  ConflictIntent,
  {
    readonly kind: "propose-pact" | "accept-pact" | "end-pact" | "send-metal";
  }
>;

export function diplomacyCommandFor(
  model: SeatModel,
  intent: DiplomacyIntent,
  target: SeatModel,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacy: RecordDefinition<DiplomacyState & JsonValue>,
  matchLog: import("@e308/core").DomainEventDefinition<MatchLogPayload & JsonValue>,
): Command<Micro> {
  if (intent.kind === "send-metal")
    return shipmentCommand(model, target, intent.amount, lifecycle, diplomacy, matchLog);
  const actionKind =
    intent.kind === "propose-pact" ? "propose" : intent.kind === "accept-pact" ? "accept" : "end";
  const message = intent.kind === "propose-pact" ? (intent.message ?? null) : null;
  return {
    id: `intent:${model.seat.id}:${intent.kind}`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      rejectIfEliminated(transaction, target);
      rejectUnlessActiveBorder(transaction, model, target, lifecycle);
      transaction.setRecord(
        diplomacy,
        appendPactAction(transaction.getRecord(diplomacy), {
          actorId: model.seat.id,
          targetId: target.seat.id,
          kind: actionKind,
          message,
        }),
      );
    },
  };
}

function shipmentCommand(
  sender: SeatModel,
  recipient: SeatModel,
  encodedAmount: string,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacy: RecordDefinition<DiplomacyState & JsonValue>,
  matchLog: import("@e308/core").DomainEventDefinition<MatchLogPayload & JsonValue>,
): Command<Micro> {
  const amount = microFromString(encodedAmount);
  if (amount <= 0n) throw new TypeError("Shipment amount must be positive");
  return {
    id: `intent:${sender.seat.id}:send-metal`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, sender);
      rejectIfEliminated(transaction, recipient);
      rejectUnlessActiveBorder(transaction, sender, recipient, lifecycle);
      const stock = transaction.get(sender.resources.metal);
      if (stock < amount)
        transaction.reject({
          code: "insufficient",
          resourceId: sender.resources.metal.id,
          required: amount,
          available: stock,
        });
      const state = transaction.getRecord(diplomacy) as DiplomacyState;
      const shipment = Object.freeze({
        id: `shipment-${state.nextShipmentId}`,
        senderId: sender.seat.id,
        recipientId: recipient.seat.id,
        amount: formatMicro(amount),
        deliversAtMs: transaction.gameTimeMs() + SHIPMENT_TRAVEL_MS,
        status: "in-transit" as const,
      });
      transaction.set(sender.resources.metal, (stock - amount) as Micro);
      transaction.setRecord(
        diplomacy,
        cloneDiplomacy({
          ...state,
          nextShipmentId: state.nextShipmentId + 1,
          shipments: [...state.shipments, shipment],
        }) as DiplomacyState & JsonValue,
      );
      transaction.emit(
        matchLog,
        shipmentPayload("metal-shipped", shipment),
        participantAudience(shipment.senderId, shipment.recipientId),
      );
    },
  };
}

export function shipmentPayload(
  kind: "metal-shipped" | "metal-delivered" | "metal-lost",
  shipment: { id: string; senderId: string; recipientId: string; amount: string },
): MatchLogPayload & JsonValue {
  return {
    kind,
    shipmentId: shipment.id,
    senderId: shipment.senderId,
    recipientId: shipment.recipientId,
    amount: shipment.amount,
  } as MatchLogPayload & JsonValue;
}

export function participantAudience(left: string, right: string) {
  return { kind: "seats" as const, seatIds: [left, right].sort() };
}
